import { getBffBaseUrl } from './bffBaseUrl';

// Session-presence detection with a host-only-cookie fallback.
//
// Fast path (vanity domains, e.g. *.horne-dev.com):
//   The BFF sets a non-HttpOnly `session_exists` cookie scoped to the shared
//   registrable parent domain, so every tenant SPA subdomain can read it
//   synchronously. We use that read and make NO network call.
//
// Fallback path (default *.azurewebsites.net hostnames, no custom domain):
//   `*.azurewebsites.net` is on the Public Suffix List, so cookies cannot be
//   Domain-scoped across hosts. The BFF's `session_exists` cookie is therefore
//   host-only on `app-be-{env}.azurewebsites.net` and is UNREADABLE from the
//   tenant SPA host (`app-fe-{env}-*.azurewebsites.net`). `getCookie` returns
//   null. In that case we can't tell from cookies alone whether a session is
//   active, so we do a single lightweight credentialed probe against the BFF
//   (`GET /api/v1/chat/me`). Cross-origin credentialed fetch DOES send the
//   BFF's host-only cookies (SameSite=None; Secure), so:
//     200  => a session exists  -> render the app
//     401  => no session (AUTH_COOKIES_MISSING) -> redirect to login
//
// This is gated on "is the cookie readable", NOT on a hardcoded hostname, so
// it works identically in both deployment modes.

const SESSION_COOKIE = 'session_exists';

// Raw cookie read. Returns the string value, or null if the cookie is not
// present/readable from this host (the host-only-cookie case above).
const readSessionCookie = () => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${SESSION_COOKIE}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const cookieMeansActive = (raw) => {
  const normalized = (raw || '').toLowerCase();
  return normalized === 'true' || normalized === '1';
};

// True only when the cookie is actually present and reads truthy. Used as the
// synchronous fast path by callers that want to skip the async probe.
export const isSessionCookieReadable = () => readSessionCookie() !== null;

export const isSessionCookieTrue = () => cookieMeansActive(readSessionCookie());

// Single credentialed probe against the BFF. Resolves true on 200 (session
// present), false on 401/any error (no session / unreachable). Never throws —
// callers treat a false result as "no session" and redirect to login, so a
// transient network error degrades to the login portal rather than a blank
// screen or a redirect loop.
const probeSession = async () => {
  const bffBase = getBffBaseUrl();
  if (!bffBase) return false;
  try {
    const response = await fetch(`${bffBase}/api/v1/chat/me`, {
      method: 'GET',
      credentials: 'include',
    });
    return response.ok; // 200 => session present; 401 => AUTH_COOKIES_MISSING
  } catch {
    return false;
  }
};

// Resolve session presence using the cookie fast path when readable, otherwise
// fall back to the credentialed probe.
//
//   cookie readable  => use its truthy value, no network call (vanity mode)
//   cookie absent    => probe the BFF (azurewebsites host-only-cookie mode)
//
// Returns a Promise<boolean>. Because a false result deterministically maps to
// "show login" (not "retry"), there is no infinite redirect loop: with no
// session the probe returns 401 -> false -> login, and the login portal does
// not bounce back here.
export const resolveSessionActive = async () => {
  const raw = readSessionCookie();
  if (raw !== null) {
    // Fast path: cookie is readable (shared-parent-domain deployment).
    return cookieMeansActive(raw);
  }
  // Cookie unreadable (host-only on the BFF host): probe to decide.
  return probeSession();
};
